import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  CatalogRoom,
  ConflictInfo,
  ConflictResolution,
  StopsellApplyPayload,
  StopsellExtraction,
} from '~/types/stopsell';

import { findAllConflicts } from '~/utils/stopsellConflicts';
import { pluralizeToString } from '~/utils/stringUtils';

import StopsellRoomGroupRow from './StopsellRoomGroupRow';

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (payload: StopsellApplyPayload) => void;
  extraction: StopsellExtraction;
  fileName: string;
  catalogRooms: Array<CatalogRoom>;
  isBlackoutError?: boolean;
  isApplying?: boolean;
}

// Composite key so resolution state is tracked per individual conflict, not shared across multiple conflicts on the same date.
export function conflictKey(roomIndex: number, dateIndex: number, conflictIndex: number): string {
  return `${roomIndex}-${dateIndex}-${conflictIndex}`;
}

function isDateDisabled(
  dateConflicts: Array<ConflictInfo>,
  dateResolutions: Array<ConflictResolution | null>,
): boolean {
  if (dateConflicts.length === 0) {
    return false;
  }
  return dateConflicts.some(
    (_, conflictIndex) => !dateResolutions[conflictIndex] || dateResolutions[conflictIndex] === 'ignored',
  );
}

function initSelected(extraction: StopsellExtraction, catalogRooms: Array<CatalogRoom>): Array<Array<boolean>> {
  return extraction.rooms.map((room) => {
    const roomBlackouts = catalogRooms.find((c) => c.id === room.roomTypeId)?.blackoutDates ?? [];
    return room.dates.map((date) => findAllConflicts(date, roomBlackouts).length === 0);
  });
}

export default function StopsellReviewModal({
  open,
  onClose,
  onApply,
  extraction,
  fileName,
  catalogRooms,
  isBlackoutError = false,
  isApplying = false,
}: Props) {
  const [selected, setSelected] = useState<Array<Array<boolean>>>(() => initSelected(extraction, catalogRooms));
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});
  const [resolutions, setResolutions] = useState<Record<string, ConflictResolution>>({});

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setResolutions({});
      setRoomTypeOverrides({});
      setSelected(initSelected(extraction, catalogRooms));
    }
    prevOpenRef.current = open;
  }, [open, extraction, catalogRooms]);

  const conflicts = useMemo<Array<Array<Array<ConflictInfo>>>>(
    () =>
      extraction.rooms.map((room, roomIndex) => {
        const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
        const roomBlackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
        return room.dates.map((date) => findAllConflicts(date, roomBlackouts));
      }),
    [extraction, catalogRooms, roomTypeOverrides],
  );

  const unresolvedConflicts = useMemo(
    () =>
      conflicts.reduce(
        (sum, roomConflicts, roomIndex) =>
          sum +
          roomConflicts.reduce(
            (dateSum, dateConflicts, dateIndex) =>
              dateSum +
              dateConflicts.filter((_, conflictIndex) => !resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)])
                .length,
            0,
          ),
        0,
      ),
    [conflicts, resolutions],
  );

  const allSelected = selected.every((room, roomIndex) =>
    room.every((v, dateIndex) => {
      const dateConflicts = conflicts[roomIndex]?.[dateIndex] ?? [];
      const dateResolutions = dateConflicts.map(
        (_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null,
      );
      return isDateDisabled(dateConflicts, dateResolutions) || v;
    }),
  );
  const anySelected = selected.some((room, roomIndex) =>
    room.some((v, dateIndex) => {
      const dateConflicts = conflicts[roomIndex]?.[dateIndex] ?? [];
      const dateResolutions = dateConflicts.map(
        (_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null,
      );
      return !isDateDisabled(dateConflicts, dateResolutions) && v;
    }),
  );
  const selectAllIndeterminate = anySelected && !allSelected;

  const allSelectedRoomsMapped = extraction.rooms.every((room, roomIndex) => {
    const isRoomSelected = selected[roomIndex]?.some(Boolean);
    if (!isRoomSelected) return true;
    return !!(room.roomTypeId ?? roomTypeOverrides[roomIndex]);
  });

  const selectedRoomCount = selected.filter((room) => room.some(Boolean)).length;
  const selectedDateCount = selected.reduce((sum, room) => sum + room.filter(Boolean).length, 0);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelected(
        extraction.rooms.map((room, roomIndex) =>
          room.dates.map((_, dateIndex) => {
            const dateConflicts = conflicts[roomIndex]?.[dateIndex] ?? [];
            const dateResolutions = dateConflicts.map(
              (_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null,
            );
            return isDateDisabled(dateConflicts, dateResolutions) ? false : checked;
          }),
        ),
      );
    },
    [extraction, conflicts, resolutions],
  );

  const handleSelectAllChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => handleSelectAll(e.target.checked),
    [handleSelectAll],
  );

  const handleRoomToggleAll = useCallback(
    (roomIndex: number, value: boolean) => {
      setSelected((prev) =>
        prev.map((room, roomIdx) =>
          roomIdx === roomIndex
            ? room.map((_, dateIndex) => {
                const dateConflicts = conflicts[roomIndex]?.[dateIndex] ?? [];
                const dateResolutions = dateConflicts.map(
                  (_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null,
                );
                return isDateDisabled(dateConflicts, dateResolutions) ? false : value;
              })
            : room,
        ),
      );
    },
    [conflicts, resolutions],
  );

  const handleDateChange = useCallback((roomIndex: number, dateIndex: number, value: boolean) => {
    setSelected((prev) =>
      prev.map((room, roomIdx) =>
        roomIdx === roomIndex ? room.map((v, dateIdx) => (dateIdx === dateIndex ? value : v)) : room,
      ),
    );
  }, []);

  const handleRoomTypeOverride = useCallback(
    (roomIndex: number, roomTypeId: string) => {
      setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: roomTypeId }));

      setResolutions((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${roomIndex}-`)) {
            delete next[key];
          }
        });
        return next;
      });

      const newRoomBlackouts = catalogRooms.find((c) => c.id === roomTypeId)?.blackoutDates ?? [];
      const room = extraction.rooms[roomIndex];
      setSelected((prev) =>
        prev.map((roomSelected, roomIdx) => {
          if (roomIdx !== roomIndex) return roomSelected;
          return room.dates.map((date) => findAllConflicts(date, newRoomBlackouts).length === 0);
        }),
      );
    },
    [catalogRooms, extraction],
  );

  const handleResolve = useCallback(
    (roomIndex: number, dateIndex: number, conflictIndex: number, status: ConflictResolution) => {
      const newResolutions = { ...resolutions, [conflictKey(roomIndex, dateIndex, conflictIndex)]: status };
      setResolutions(newResolutions);

      setSelected((prev) =>
        prev.map((room, roomIdx) =>
          roomIdx === roomIndex
            ? room.map((v, dateIdx) => {
                if (dateIdx !== dateIndex) return v;
                const dateConflicts = conflicts[roomIdx]?.[dateIdx] ?? [];
                const anyIgnored = dateConflicts.some(
                  (_, conflictIdx) => newResolutions[conflictKey(roomIdx, dateIdx, conflictIdx)] === 'ignored',
                );
                if (anyIgnored) {
                  return false;
                }
                const allResolved = dateConflicts.every(
                  (_, conflictIdx) => !!newResolutions[conflictKey(roomIdx, dateIdx, conflictIdx)],
                );
                return allResolved;
              })
            : room,
        ),
      );
    },
    [conflicts, resolutions],
  );

  const handleUndo = useCallback((roomIndex: number, dateIndex: number, conflictIndex: number) => {
    setResolutions((prev) => {
      const next = { ...prev };
      delete next[conflictKey(roomIndex, dateIndex, conflictIndex)];
      return next;
    });
    setSelected((prev) =>
      prev.map((room, roomIdx) =>
        roomIdx === roomIndex ? room.map((v, dateIdx) => (dateIdx === dateIndex ? false : v)) : room,
      ),
    );
  }, []);

  const handleApply = useCallback(() => {
    type ResolvedDate = StopsellApplyPayload['rooms'][number]['resolvedDates'][number];

    const payload: StopsellApplyPayload = {
      rooms: extraction.rooms
        .map((room, roomIndex) => {
          const roomTypeId = roomTypeOverrides[roomIndex] ?? room.roomTypeId;
          if (!roomTypeId) return null;

          const resolvedDates = room.dates.reduce<ResolvedDate[]>((acc, date, dateIndex) => {
            if (!selected[roomIndex][dateIndex]) return acc;

            const dateConflicts = conflicts[roomIndex]?.[dateIndex] ?? [];
            const dateResolutions = dateConflicts.map(
              (_, conflictIndex) => resolutions[conflictKey(roomIndex, dateIndex, conflictIndex)] ?? null,
            );

            const rangesToMerge = [
              date,
              ...dateConflicts.filter((_, i) => dateResolutions[i] === 'extending').map((c) => c.existingRange),
            ];
            const range =
              rangesToMerge.length > 1
                ? {
                    start: rangesToMerge.reduce((min, r) => (r.start < min ? r.start : min), date.start),
                    end: rangesToMerge.reduce((max, r) => (r.end > max ? r.end : max), date.end),
                  }
                : date;

            const hasExtending = dateResolutions.some((r) => r === 'extending');
            const hasOverriding = dateResolutions.some((r) => r === 'overriding');
            const resolution = hasExtending ? 'extending' : hasOverriding ? 'overriding' : 'new';

            const conflictBlocks = dateConflicts
              .filter((_, i) => dateResolutions[i] === 'overriding' || dateResolutions[i] === 'extending')
              .map((c) => ({ blocks: c.existingRange.blocks }));

            acc.push({ range, resolution, conflicts: conflictBlocks });
            return acc;
          }, []);

          if (resolvedDates.length === 0) return null;
          return { roomTypeId, resolvedDates };
        })
        .filter((room): room is NonNullable<typeof room> => room !== null),
    };
    onApply(payload);
  }, [extraction, onApply, roomTypeOverrides, selected, conflicts, resolutions]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Stopsell Dates</Typography>
        </Stack>
        <Chip icon={<DescriptionOutlinedIcon />} label={fileName} size="small" sx={{ mt: 1 }} />
      </DialogTitle>

      <DialogContent dividers>
        {isBlackoutError ? (
          <Alert severity="error">Could not load existing blackout dates. Please close and try again.</Alert>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                mb: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                  />
                }
                label={<Typography fontWeight={500}>Select all rooms</Typography>}
              />
            </Box>

            <Stack gap={1.5}>
              {extraction.rooms.map((room, roomIndex) => (
                <StopsellRoomGroupRow
                  key={`${room.name}-${roomIndex}`}
                  roomIndex={roomIndex}
                  room={room}
                  selected={selected[roomIndex]}
                  catalogRooms={catalogRooms}
                  selectedRoomTypeId={roomTypeOverrides[roomIndex] ?? room.roomTypeId ?? ''}
                  roomConflicts={conflicts[roomIndex]}
                  resolutions={resolutions}
                  onDateChange={handleDateChange}
                  onToggleAll={handleRoomToggleAll}
                  onRoomTypeChange={handleRoomTypeOverride}
                  onResolve={handleResolve}
                  onUndo={handleUndo}
                />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {unresolvedConflicts > 0 && (
            <Typography variant="body2" color="warning.dark">
              <strong>
                {unresolvedConflicts} unresolved {unresolvedConflicts === 1 ? 'conflict' : 'conflicts'}
              </strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            <strong>{pluralizeToString('room', selectedRoomCount)}</strong>,{' '}
            <strong>{pluralizeToString('date range', selectedDateCount)}</strong> selected
          </Typography>
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Tooltip
            title={
              isBlackoutError
                ? 'Blackout dates could not be loaded. Please close and try again.'
                : anySelected && !allSelectedRoomsMapped
                ? 'All selected rooms must be mapped to a room type'
                : unresolvedConflicts > 0
                ? 'Resolve all conflicts before applying'
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={
                  isApplying || isBlackoutError || !anySelected || !allSelectedRoomsMapped || unresolvedConflicts > 0
                }
              >
                {isApplying ? 'Applying...' : 'Apply Stopsell'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
